import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { MercenaryCard } from '../components/mercenaries/MercenaryCard';
import { MercenaryProfileModal } from '../components/mercenaries/MercenaryProfileModal';
import { AuthModal } from '../components/auth/AuthModal';
import { ReportModal } from '../components/auth/ReportModal';
import { AdSlot } from '../components/ui/AdSlot';
import { useStore, getUsers } from '../store';
import { t } from '../lib/i18n';

export const MercenariesPage: React.FC = () => {
  const { currentUser, startDirectChat, setActiveChatId, lang } = useStore();
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<any>(null);
  const [index, setIndex] = useState(0);
  const [showAd, setShowAd] = useState(false);
  const navigate = useNavigate();

  const users = getUsers();

  const filtered = useMemo(() => {
    return users.filter(u => {
      if (u.blocked) return false;
      const q = query.toLowerCase();
      const nameMatch = !q ||
        (u.firstName + ' ' + u.lastName).toLowerCase().includes(q) ||
        u.skills.some(s => s.name.toLowerCase().includes(q)) ||
        u.bio.toLowerCase().includes(q);
      const countryMatch = !country || u.country.toLowerCase().includes(country.toLowerCase());
      const cityMatch = !city || u.city.toLowerCase().includes(city.toLowerCase());
      return nameMatch && countryMatch && cityMatch;
    });
  }, [users, query, country, city]);

  const handleMessage = (userId: string) => {
    if (!currentUser) { setAuthOpen(true); return; }
    const chatId = startDirectChat(userId);
    setActiveChatId(chatId);
    navigate('/chats');
  };

  const goNext = () => {
    if (showAd) {
      setShowAd(false);
      setIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (index < filtered.length - 1) {
      setShowAd(true);
    }
  };
  const goPrev = () => {
    if (showAd) {
      setShowAd(false);
    } else {
      setIndex(i => Math.max(i - 1, 0));
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: goNext,
    onSwipedRight: goPrev,
    trackMouse: true,
  });

  const current = filtered[index];

  return (
    <div className="page-container">
      <div className="section-header">
        <h2>{t(lang, 'mercs_title')}</h2>
      </div>

      <div className="search-bar">
        <input
          className="search-input"
          type="search"
          placeholder={t(lang, 'mercs_search')}
          value={query}
          onChange={e => { setQuery(e.target.value); setIndex(0); setShowAd(false); }}
          aria-label="Search mercenaries"
        />
        <input
          className="search-input"
          type="text"
          placeholder={t(lang, 'mercs_country')}
          value={country}
          onChange={e => { setCountry(e.target.value); setIndex(0); setShowAd(false); }}
          aria-label="Filter by country"
          style={{ maxWidth: '150px' }}
        />
        <input
          className="search-input"
          type="text"
          placeholder={t(lang, 'mercs_city')}
          value={city}
          onChange={e => { setCity(e.target.value); setIndex(0); setShowAd(false); }}
          aria-label="Filter by city"
          style={{ maxWidth: '130px' }}
        />
      </div>

      <p className="results-count">
        {filtered.length} {lang === 'fr' ? 'personnes trouvées' : lang === 'es' ? 'personas encontradas' : 'people found'}
        {filtered.length > 0 ? ' -- ' + (index + 1) + ' / ' + filtered.length : ''}
      </p>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <span className="icon">no-results</span>
          <p>{t(lang, 'empty_mercs')}</p>
        </div>
      ) : (
        <div className="swipe-stage" {...swipeHandlers}>
          <button className="swipe-arrow swipe-arrow-left" onClick={goPrev} aria-label="Previous">&lsaquo;</button>

          {showAd ? (
            <div className="swipe-card-wrap ad-card">
              <AdSlot slot="2222222222" format="rectangle" />
              <p className="ad-skip-hint">Tap arrow to continue</p>
            </div>
          ) : (
            <div className="swipe-card-wrap">
              <MercenaryCard
                user={current}
                onViewProfile={() => setSelectedUser(current)}
                onMessage={() => handleMessage(current.id)}
                onReport={() => setReportTarget({ id: current.id, name: current.firstName + ' ' + current.lastName })}
              />
            </div>
          )}

          <button className="swipe-arrow swipe-arrow-right" onClick={goNext} aria-label="Next">&rsaquo;</button>
        </div>
      )}

      <MercenaryProfileModal
        user={selectedUser}
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        onMessage={handleMessage}
        onReport={(id, name) => setReportTarget({ id, name })}
      />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <ReportModal
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        targetId={reportTarget ? reportTarget.id : null}
        targetName={reportTarget ? reportTarget.name : undefined}
      />
    </div>
  );
};
